import { useTranslation } from '@dukanti/shared';
import type { AdminShippingLabelViewModel, TranslationKey } from '@dukanti/shared';

export interface ShippingLabelPreviewProps {
  readonly viewModel: AdminShippingLabelViewModel;
  readonly dir: 'rtl' | 'ltr';
}

interface LabelRowProps {
  readonly labelKey: TranslationKey;
  readonly children: React.ReactNode;
  readonly testId: string;
  readonly valueDir?: 'rtl' | 'ltr' | 'auto';
}

function LabelRow({
  labelKey,
  children,
  testId,
  valueDir = 'auto',
}: LabelRowProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="border-border/80 grid grid-cols-[28mm_minmax(0,1fr)] gap-2 border-b py-1.5 last:border-b-0">
      <dt className="text-muted-foreground text-[10px] font-semibold uppercase leading-tight">
        {t(labelKey)}
      </dt>
      <dd
        dir={valueDir}
        data-testid={testId}
        className="text-foreground min-w-0 text-start text-[12px] font-medium leading-snug [overflow-wrap:anywhere]"
      >
        {children}
      </dd>
    </div>
  );
}

export function ShippingLabelPreview({
  viewModel,
  dir,
}: ShippingLabelPreviewProps): React.JSX.Element {
  const { t } = useTranslation();
  const hasCourierMetadata = viewModel.packageTag || viewModel.batchCode;
  const batchValue =
    viewModel.batchCode && viewModel.batchOrderCount
      ? `${viewModel.batchCode} · ${viewModel.batchOrderCount}`
      : viewModel.batchCode;

  return (
    <section
      dir={dir}
      data-testid="shipping-label-print-root"
      className="bg-card text-card-foreground shadow-admin-card mx-auto flex h-[150mm] w-[100mm] max-w-full flex-col overflow-hidden rounded-lg border border-black/20 p-[6mm]"
      aria-label={t('admin.orders.shippingLabel.previewHeading')}
    >
      <header className="border-foreground/20 border-b pb-3 text-start">
        <p className="text-muted-foreground text-[10px] font-semibold uppercase leading-none">
          {t('admin.orders.shippingLabel.title')}
        </p>
        <h1
          dir="ltr"
          className="mt-1 text-start font-mono text-[22px] font-bold leading-tight tracking-normal [overflow-wrap:anywhere]"
        >
          {viewModel.packageTag ?? viewModel.orderReference}
        </h1>
      </header>

      <dl className="mt-3 min-h-0 flex-1 overflow-hidden text-start">
        {viewModel.packageTag ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.package"
            testId="shipping-label-field-package"
            valueDir="ltr"
          >
            {viewModel.packageTag}
          </LabelRow>
        ) : null}

        {batchValue ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.batch"
            testId="shipping-label-field-batch"
            valueDir="ltr"
          >
            {batchValue}
          </LabelRow>
        ) : null}

        {!hasCourierMetadata ? (
          <div className="border-border/80 text-muted-foreground border-b py-1.5 text-start text-[11px] leading-snug">
            {t('admin.orders.shippingLabel.noCourierAssignment')}
          </div>
        ) : null}

        <LabelRow
          labelKey="admin.orders.shippingLabel.fields.order"
          testId="shipping-label-field-order"
          valueDir="ltr"
        >
          {viewModel.orderReference}
        </LabelRow>
        <LabelRow
          labelKey={
            viewModel.packageTotalLabelKey === 'fulfillment.legacySubtotal'
              ? 'fulfillment.legacySubtotal'
              : 'checkout.total'
          }
          testId="shipping-label-field-package-total"
          valueDir="ltr"
        >
          {viewModel.packageTotal}
        </LabelRow>
        <LabelRow
          labelKey="admin.orders.shippingLabel.fields.recipient"
          testId="shipping-label-field-recipient"
        >
          {viewModel.recipientName}
        </LabelRow>
        {viewModel.recipientPhone ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.phone"
            testId="shipping-label-field-phone"
            valueDir="ltr"
          >
            {viewModel.recipientPhone}
          </LabelRow>
        ) : null}
        {viewModel.destination ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.destination"
            testId="shipping-label-field-destination"
          >
            {viewModel.destination}
          </LabelRow>
        ) : null}
        {viewModel.addressLines.length > 0 ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.deliveryInformation"
            testId="shipping-label-field-address"
          >
            <span className="block">
              {viewModel.addressLines.map((line) => (
                <span key={line} className="block [overflow-wrap:anywhere]">
                  {line}
                </span>
              ))}
            </span>
          </LabelRow>
        ) : null}
        {viewModel.senderName ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.sender"
            testId="shipping-label-field-sender"
          >
            {viewModel.senderName}
          </LabelRow>
        ) : null}
        {viewModel.senderPhone ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.senderPhone"
            testId="shipping-label-field-sender-phone"
            valueDir="ltr"
          >
            {viewModel.senderPhone}
          </LabelRow>
        ) : null}
        {viewModel.deliveryNotes ? (
          <LabelRow
            labelKey="admin.orders.shippingLabel.fields.notes"
            testId="shipping-label-field-notes"
          >
            <span className="line-clamp-3">{viewModel.deliveryNotes}</span>
          </LabelRow>
        ) : null}
        <LabelRow
          labelKey="admin.orders.shippingLabel.fields.printedAt"
          testId="shipping-label-field-printed-at"
        >
          {viewModel.printedAt}
        </LabelRow>
      </dl>

      <footer className="border-foreground/20 mt-3 border-t pt-2 text-start">
        <p className="text-muted-foreground text-[10px] font-medium leading-snug">
          {t('admin.orders.shippingLabel.singleOrderNotice')}
        </p>
      </footer>
    </section>
  );
}
